import React, { useState, useEffect } from 'react';
import { Modal, Badge, Spinner, Alert, Card } from 'react-bootstrap';
import { User, Activity } from 'lucide-react';
import { apiService } from '../services/api';

const UserLogsModal = ({ show, onHide, user }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show && user) {
      fetchUserLogs();
    }
  }, [show, user]);

  const fetchUserLogs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiService.get(`/api/user-logs/user/${user.id}`);
      
      if (response.status) {
        setLogs(response.data.logs);
      } else {
        setError('Failed to fetch user logs');
      }
    } catch (err) {
      console.error('Error fetching user logs:', err);
      setError(err.message || 'Failed to fetch user logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionDescription = (action) => {
    const { action: actionType, metadata } = action;
    
    // Create user-friendly descriptions based on action type
    switch (actionType) {
      case 'login':
        return {
          title: 'User Login',
          description: metadata?.email 
            ? `Logged in as ${metadata.email}`
            : 'User logged into the system',
          details: metadata?.rememberMe 
            ? `Session duration: ${metadata.tokenExpiration || '8 hours'}`
            : null
        };
      
      case 'logout':
        return {
          title: 'User Logout',
          description: 'User logged out of the system',
          details: null
        };
      
      case 'password_change':
        return {
          title: 'Password Changed',
          description: 'User changed their password',
          details: null
        };
      
      case 'profile_update':
        return {
          title: 'Profile Updated',
          description: metadata?.updatedFields && metadata.updatedFields.length > 0
            ? `Updated: ${metadata.updatedFields.map(field => {
                // Convert camelCase to readable format
                return field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
              }).join(', ')}`
            : 'User updated their profile information',
          details: null
        };
      
      default:
        return {
          title: actionType ? actionType.charAt(0).toUpperCase() + actionType.slice(1).replace(/_/g, ' ') : 'Action',
          description: metadata?.api 
            ? `Performed action via ${metadata.api}`
            : 'User performed an action',
          details: metadata?.method ? `Method: ${metadata.method}` : null
        };
    }
  };

  const renderActions = (actions) => {
    if (!actions || !Array.isArray(actions)) {
      return <span className="text-muted">No actions recorded</span>;
    }

    return (
      <div>
        {actions.map((action, index) => {
          const actionInfo = getActionDescription(action);
          
          return (
            <Card key={index} className="mb-2">
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <Badge bg="primary" className="me-2">
                        {actionInfo.title}
                      </Badge>
                      <small className="text-muted">
                        {new Date(action.timestamp).toLocaleString()}
                      </small>
                    </div>
                    <div className="text-dark small">
                      {actionInfo.description}
                    </div>
                    {actionInfo.details && (
                      <div className="mt-1">
                        <small className="text-muted">{actionInfo.details}</small>
                      </div>
                    )}
                  </div>
                  {action.metadata?.success !== false && (
                    <Badge bg="success" className="ms-2">Success</Badge>
                  )}
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  };

  if (!user) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <User size={20} className="me-2" />
          User Activity Logs - {user.firstName} {user.lastName}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2">Loading user logs...</div>
          </div>
        )}

        {!loading && !error && (
          <>
            {logs.length === 0 ? (
              <Alert variant="info" className="text-center">
                <Activity size={20} className="me-2" />
                No activity logs found for this user.
              </Alert>
            ) : (
              <div>
                {logs.map((log, index) => (
                  <Card key={log.id} className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Session Date:</strong> {new Date(log.createdAt).toLocaleDateString()}
                      </div>
                      <Badge bg="light" text="dark">
                        {log.actions?.length || 0} actions
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      {renderActions(log.actions)}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={onHide}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserLogsModal;
